import { nodeModuleSources } from "./nodeModules/registry";

/**
 * Загружает Node-модуль из src/utils/nodeModules:
 * исходник берется из бандла, обфусцируется во временный файл,
 * загружается в память и после этого удаляется с диска.
 */

const normalizeModuleName = (value) =>
  String(value || "").replace(/[^a-zA-Z0-9]/g, "").toLowerCase();

const getNodeContext = () => {
  const nodeRequire = window.require || (window.parent && window.parent.require);
  const cep = window.__adobe_cep__ || (window.parent && window.parent.__adobe_cep__);

  if (!nodeRequire || !cep) {
    return null;
  }

  return { nodeRequire, cep };
};

const getBundledModuleSource = (moduleName) => {
  const requestedName = normalizeModuleName(moduleName);

  for (const [name, source] of Object.entries(nodeModuleSources)) {
    if (normalizeModuleName(name) === requestedName) {
      return source;
    }
  }

  throw new Error(`Bundled module "${moduleName}" not found`);
};

const createObfuscatedModuleSource = (nodeRequire, moduleSource) => {
  const encodedSource = nodeRequire("buffer").Buffer.from(moduleSource, "utf8").toString(
    "base64",
  );

  return [
    '"use strict";',
    `const encodedSource = ${JSON.stringify(encodedSource)};`,
    "const decodedSource = Buffer.from(encodedSource, 'base64').toString('utf8');",
    "const factory = new Function('exports', 'require', 'module', '__filename', '__dirname', decodedSource);",
    // require("./name") of a module also bundled here: that module (the sources require their siblings, the temp
    // file has none next to it); anything else: Node's require
    "const siblings = process.__ae2NodeModuleSiblings;",
    "const localRequire = function (id) {",
    "  const name = id.indexOf('./') === 0 ? id.slice(2).replace(/[.]js$/, '') : null;",
    "  if (name && siblings && siblings.has(name)) return siblings.load(name);",
    "  return require(id);",
    "};",
    "localRequire.resolve = require.resolve;",
    "localRequire.cache = require.cache;",
    "factory(exports, localRequire, module, __filename, __dirname);",
    "",
  ].join("\n");
};

const cleanupModuleCache = (nodeRequire, filePath) => {
  try {
    const resolvedPath = nodeRequire.resolve(filePath);
    if (nodeRequire.cache[resolvedPath]) {
      delete nodeRequire.cache[resolvedPath];
    }
  } catch (error) {
    // Файл может еще не существовать в кеше.
  }
};

export const loadLocalModule = (moduleName) => {
  let tempFilePath = null;
  let nodeRequire = null;

  try {
    const context = getNodeContext();

    if (!context) {
      alert("Node.js environment not detected");
      return null;
    }

    ({ nodeRequire } = context);
    // the bundled modules a module may require as "./name" (createObfuscatedModuleSource)
    nodeRequire("process").__ae2NodeModuleSiblings = {
      has: (name) => Object.keys(nodeModuleSources).some((n) => normalizeModuleName(n) === normalizeModuleName(name)),
      load: (name) => {
        const loaded = loadLocalModule(name);
        if (!loaded) throw new Error(`Bundled module "${name}" could not be loaded`);
        return loaded;
      },
    };

    const sourceCode = getBundledModuleSource(moduleName);
    const fs = nodeRequire("fs");
    const os = nodeRequire("os");
    const path = nodeRequire("path");
    const crypto = nodeRequire("crypto");
    const obfuscatedCode = createObfuscatedModuleSource(nodeRequire, sourceCode);

    const tempDir = path.join(os.tmpdir(), "Harchenko", "node-modules-cache");
    const tempFileName = `${moduleName.replace(/[^\w.-]+/g, "_")}-${crypto
      .randomBytes(8)
      .toString("hex")}.js`;

    tempFilePath = path.join(tempDir, tempFileName);

    fs.mkdirSync(tempDir, { recursive: true });
    fs.writeFileSync(tempFilePath, obfuscatedCode, "utf8");

    cleanupModuleCache(nodeRequire, tempFilePath);
    const loadedModule = nodeRequire(tempFilePath);
    cleanupModuleCache(nodeRequire, tempFilePath);

    return loadedModule;
  } catch (error) {
    alert(`Error loading module ${moduleName}: ${error.message}`);
    return null;
  } finally {
    if (!tempFilePath || !nodeRequire) {
      return;
    }

    try {
      const fs = nodeRequire("fs");
      cleanupModuleCache(nodeRequire, tempFilePath);

      if (fs.existsSync(tempFilePath)) {
        fs.unlinkSync(tempFilePath);
      }
    } catch (cleanupError) {
      // Игнорируем ошибки очистки временного файла.
    }
  }
};